<template>
  <VCard v-if="!!cartStore.summary.potm" class="potm-progress-card bg-surface" variant="outlined">
    <VCardText class="pa-4">
      <VRow no-gutters align="start">
        <!-- Icon only for cart variant-->
        <VCol v-if="isCart" cols="auto" class="hidden-xs mr-3 fill-height ">
          <VIcon
            size="42"
            color="white"
            class="potm-icon"
          >
            {{ icons.mdiCreation }}
          </VIcon>
        </VCol>
        <!-- Title and Progress Bar -->
        <VCol>
          <VRow no-gutters align="center">
            <VCol cols="12">
              <span
                :id="potmTitleId"
                class="potm-title"
                :class="isCart ? 'potm-title--cart' : 'potm-title--flyout'"
              >
                {{ $t('label.productOfTheMonth') }}
              </span>
            </VCol>
            <VCol
              class="potm-progress-bar d-flex align-center flex-grow-1"
              :class="isCart ? 'potm-progress-bar--cart' : 'potm-progress-bar--flyout'"
            >
              <VProgressLinear
                :model-value="progressPercentage"
                :aria-labelledby="potmTitleId"
                bg-color="border"
                :bg-opacity="1"
                color="userBackgroundColor"
                height="12"
                rounded
              />
              <span class="ml-3 flex-grow-1">{{ potmSummary.formattedPotmThreshold }}</span>
            </VCol>

            <!-- Status Message flyout variant and mobile for cart variant-->
            <VCol
              cols="12"
              class="potm-message"
              :class="{
                'font14 hidden-sm-and-up': isCart,
                'font12': isFlyout
              }"
            >
              <VHtml :html="statusMessage" />
            </VCol>

            <!-- Spacing between progress bar and button -->
            <VSpacer
              v-if="isCart"
              class="shrink mx-3 hidden-xs"
            />

            <!-- Action Button (Shop Now or Sign In) -->
            <VCol
              v-if="shouldShowShopNow || shouldShowSignIn"
              class="px-0 potm-button"
              :class="{ 'potm-button--cart': isCart }"
            >
              <SBtn
                :color="buttonColor"
                :variant="isOutlined ? 'outlined' : 'flat'"
                block
                :size="isCart ? 'large' : 'default'"
                class="rounded-lg font16 font-weight-bold px-12"
                :class="[
                  (breakpoints.smAndUp.value && isCart) ? 'potm-button-style text-black' : 'text-primary',
                  (breakpoints.xs.value || isFlyout) ? 'mt-6' : ''
                ]"
                @click="shouldShowShopNow ? navigateToPotm() : handleSignIn()"
              >
                {{ shouldShowShopNow ? $t('btn.shopNow') : $t('pages.auth-sign-in') }}
              </SBtn>
            </VCol>

            <!-- Status Message Desktop for cart variant -->
            <VCol
              v-if="isCart"
              cols="12"
              class="font14 hidden-xs"
            >
              <VHtml :html="statusMessage" />
            </VCol>
          </VRow>
        </VCol>
      </VRow>
    </VCardText>

    <!-- Auth Dialog for cart mode -->
    <AppAuthDialog
      v-if="showDialog"
      v-model="showDialog"
      :register="false"
      color="black"
      register-btn
      @authenticated="onAuthenticated"
    />
  </VCard>
</template>

<script lang="ts" setup>
import { mdiCreation } from '@mdi/js'
import VHtml from '~/components/base/VHtml.vue'
import AppAuthDialog from '~/components/auth/AuthDialog.vue'
import { useCartStore } from '~/stores/cart'
import { useCategoryUrl } from '~/composables/useCategoryUrl'
import { usePotmState } from '~/composables/usePotmState'
import type { PotmSummary } from '~/types/cart'
import { PRODUCT_OF_THE_MONTH } from '~/utils/categoryRoutes'

const props = withDefaults(defineProps<{
  variant?: 'cart' | 'flyout'
}>(), {
  variant: 'cart'
})

const icons = { mdiCreation }
const cartStore = useCartStore()
const router = useRouter()
const route = useRoute()
const { buildCategoryUrl } = useCategoryUrl()

// Use POTM state composable (no product needed for progress bar)
const potmState = usePotmState()
const { isAnonymousUser } = potmState

const showDialog = ref(false)

// Vuetify supplies role="progressbar" and aria-valuenow, but no accessible name — without
// one the bar announces as a bare percentage. Generated rather than hardcoded because the
// cart and flyout variants can both be mounted on the same page. AB#63253
const potmTitleId = useId()

const isCart = computed(() => props.variant === 'cart')
const isFlyout = computed(() => props.variant === 'flyout')

const potmSummary = computed(() => cartStore.summary.potm || {} as PotmSummary)
const isQualified = computed(() => potmSummary.value.isPotmEligible)
const shouldShowShopNow = computed(() => isQualified.value && !isAnonymousUser.value)
const shouldShowSignIn = computed(() => isAnonymousUser.value)

const { breakpoints } = useBreakpoints()
const buttonColor = computed(() => {
  if (breakpoints.smAndUp.value && isCart.value) {
    // Deliberately no color on this branch. On an `outlined` variant Vuetify
    // routes `color` to a FOREGROUND utility (`.text-white`), not a background
    // one, and that utility lands in the `vuetify-utilities` layer. Every SFC
    // <style> block is wrapped into `app-components`, which layers.scss orders
    // BEFORE `vuetify-utilities` — and layer order outranks specificity — so a
    // scoped rule here can never beat it. Emitting no color utility leaves the
    // template's `text-black` (same layer, uncontested) to set the label, which
    // is what keeps this branch legible. The white background and black border
    // still come from `potm-button-style`; Vuetify declares no competing values
    // for those properties, so that half of the scoped rule does apply.
    return undefined
  }
  return 'primary' // Primary color for mobile or flyout
})

const isOutlined = computed(() => {
  // Desktop cart: white/black/bordered for WCAG 1.4.3 contrast. Was the coral
  // `userBackgroundColor` (#F37965) under white text at 2.72:1; now 21:1.
  // Matches the existing flyout variant.
  return breakpoints.xs.value || isFlyout.value || (breakpoints.smAndUp.value && isCart.value)
})

const progressPercentage = computed(() => {
  if (!potmSummary.value.potmThreshold) { return 0 }
  return Math.min(100, ((potmSummary.value.potmThreshold - potmSummary.value.neededToEarnPotm) / potmSummary.value.potmThreshold) * 100)
})

const i18n = useI18n()
const statusMessage = computed(() => {
  if (isAnonymousUser.value) {
    return i18n.t('cart.p.potm.unauthenticated')
  }
  if (isQualified.value) {
    return i18n.t('cart.p.potm.qualified')
  }
  return i18n.t('cart.p.potm.notQualified', { amountNeeded: potmSummary.value.formattedNeededToEarnPotm })
})

const navigateToPotm = () => {
  router.push(buildCategoryUrl(PRODUCT_OF_THE_MONTH))
}

const handleSignIn = () => {
  if (isCart.value) {
    // In cart mode: show auth dialog (same behavior as checkout)
    showDialog.value = true
  } else {
    // In flyout mode: redirect to sign-in page with return URL
    router.push({
      path: '/auth/sign-in',
      query: { redirectOnSuccess: route.fullPath }
    })
  }
}

const onAuthenticated = () => {
  showDialog.value = false
}
</script>

<style lang="scss" scoped>
@use '~/assets/scss/settings';
@use 'sass:map';

.potm-progress-card {
  border: 2px solid rgb(var(--v-theme-userBackgroundColor));
  border-radius: 8px;
  margin-bottom: 16px;

  .potm-icon {
    background-color: rgb(var(--v-theme-userBackgroundColor));
    border-radius: 50%;
    padding: 8px;
  }
.potm-title {
  font-weight: bold;

  &--cart {
    font-size: 22px;
    text-transform: uppercase;
    margin-bottom: 12px;
  }

  &--flyout {
    font-size: 16px;
  }
}
.potm-progress-bar {
  &--cart{
    margin: 12px 0;
  }

  &--flyout {
    margin: 8px 0;
  }
}
  .potm-button {
  @media #{map.get(settings.$display-breakpoints, 'sm-and-up')} {
    &--cart {
      flex-grow: 0;
      flex-shrink: 1;
    }
    }
  }
}

// Desktop cart branch: shape only — this rule deliberately sets no `color`.
//
// Every SFC <style> block is wrapped into `@layer app-components` by the
// postcss plugin in appbuild/css-layers, and layers.scss orders that layer
// BEFORE `vuetify-utilities`. Layer order is resolved ahead of specificity, so
// a `color` declared here loses to ANY Vuetify foreground utility no matter how
// specific the selector — a `[data-v-*]`-scoped rule included. The label color
// is therefore settled in the template instead: the desktop branch emits
// `text-black` and passes no `color` prop, so no competing `.text-white` is
// generated and `text-black` wins uncontested within its own layer.
//
// `border` and `background-color` DO apply from here, because Vuetify declares
// no competing values for those properties in a later layer.
:deep(.potm-button-style) {
  border: 1px solid black;
  background-color: white;
}
</style>
